import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { MapPin, Clock, Wallet, CalendarDays, CheckCircle2, Bookmark, ArrowLeft, Star, Users, Download, PhoneCall, MonitorSmartphone, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import api, { formatApiError } from "@/lib/api";
import CourseCard, { feeLabel } from "@/components/CourseCard";
import InquiryModal from "@/components/InquiryModal";
import SyllabusLeadModal from "@/components/SyllabusLeadModal";
import CallbackModal from "@/components/CallbackModal";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";
import { useSEO, JsonLd } from "@/lib/seo";
import SectionsRenderer from "@/components/admin/SectionRenderer";
import DefaultTemplateRenderer from "@/components/admin/template/DefaultTemplateRenderer";
import { useClientReady } from "@/hooks/useClientReady";

export default function ProgramDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [program, setProgram] = useState(() =>
    (window.INITIAL_DATA?.type === "program" && window.INITIAL_DATA.payload?.slug === slug)
      ? window.INITIAL_DATA.payload : null);
  const [category, setCategory] = useState(null);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [callbackOpen, setCallbackOpen] = useState(false);
  const [brochureOpen, setBrochureOpen] = useState(false);
  const [gateFile, setGateFile] = useState("");
  const [applying, setApplying] = useState(false);
  const [saved, setSaved] = useState(false);
  const clientReady = useClientReady();

  useEffect(() => {
    api.get(`/programs/${slug}`).then((r) => {
      setProgram(r.data);
      trackEvent("program_view", { slug });
      if (r.data.category_slug) api.get(`/categories/${r.data.category_slug}`).then((c) => setCategory(c.data)).catch(() => {});
    }).catch(() => setProgram((current) => (current?.slug === slug ? current : false)));
  }, [slug]);

  const seo = program?.seo || {};
  useSEO(program ? {
    title: seo.meta_title || `${program.title} — ${program.university} | Training Basket`,
    description: seo.meta_description || program.description?.slice(0, 155),
    canonical: seo.canonical_url || (program.url_path ? `https://www.trainingbasket.in${program.url_path}` : `${window.location.origin}/courses/${slug}`),
    image: seo.og_image || program.image,
    ogTitle: seo.og_title,
    ogDescription: seo.og_description,
    keywords: seo.keywords,
    noindex: !!seo.noindex,
    nofollow: !!seo.nofollow,
  } : {});

  useEffect(() => {
    if (user) api.get("/student/saved").then((r) => setSaved(r.data.programs.some((p) => p.slug === slug))).catch(() => {});
  }, [user, slug]);

  const apply = async () => {
    if (!user) {
      navigate(`/signup?next=${encodeURIComponent(`/courses/${slug}`)}&apply=1`);
      return;
    }
    setApplying(true);
    try {
      const { data } = await api.post("/applications", { program_slug: slug });
      trackEvent("application_start", { program_slug: slug });
      navigate(`/profile?tab=browse&step=batch&program=${slug}&app=${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setApplying(false);
    }
  };

  const toggleSave = async () => {
    if (!user) { toast.info("Log in to save programs"); return; }
    try {
      if (saved) { await api.delete(`/student/saved/${slug}`); setSaved(false); }
      else { await api.post(`/student/saved/${slug}`); setSaved(true); toast.success("Saved"); }
    } catch (e) { toast.error("Could not update saved list"); }
  };

  if (program === false) return <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="program-not-found"><p className="font-bold text-xl">Program not found</p><Link to="/courses" className="text-rose-600 font-semibold text-sm mt-2 inline-block">Back to catalog</Link></div>;
  if (!program) return <div className="max-w-7xl mx-auto px-6 py-16"><div className="h-96 rounded-3xl bg-slate-100 animate-pulse" /></div>;

  const faqs = (category?.faqs || []).concat([
    { q: "What is the application fee?", a: `The application/enrollment fee is ₹${Number(program.application_fee).toLocaleString("en-IN")}, credited toward your program fee on enrollment.` },
    { q: "Is EMI available?", a: program.emi_starts_at ? `Yes — EMI plans start at ₹${Number(program.emi_starts_at).toLocaleString("en-IN")}/month. A counselor can structure a plan for you.` : "Yes, installment plans are available for most programs. Talk to a counselor for a structured plan." },
  ]);

  const hasSections = (program.sections || []).some((s) => s.visible !== false && s.published !== false);

  if (program.template === "default" && (program.template_sections || []).length) {
    const selfCanon = !program.url_path && !(seo.canonical_url || "").trim();
    const tplFaqs = (program.template_sections || []).flatMap((s) =>
      s.type === "t_faq" && s.enabled !== false
        ? (s.content?.categories || []).flatMap((cat) => (cat.faqs || []).filter((f) => f.q && f.a))
        : []);
    return (
      <div data-testid="program-detail-template">
        {selfCanon && (
          <JsonLd data={{
            "@context": "https://schema.org", "@type": "Course",
            name: program.title,
            description: seo.meta_description || program.short_description || program.description,
            provider: { "@type": "Organization", name: "Training Basket", sameAs: "https://www.trainingbasket.in" },
            url: `https://www.trainingbasket.in/courses/${slug}`,
          }} />
        )}
        {tplFaqs.length > 0 && (
          <JsonLd data={{
            "@context": "https://schema.org", "@type": "FAQPage",
            mainEntity: tplFaqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
          }} />
        )}
        <DefaultTemplateRenderer sections={program.template_sections} onApply={apply} onCallback={() => setCallbackOpen(true)} onBrochure={(fileUrl) => { setGateFile(typeof fileUrl === "string" ? fileUrl : ""); setBrochureOpen(true); }} />
        <CallbackModal open={callbackOpen} onOpenChange={setCallbackOpen} programSlug={slug} programTitle={program.title} />
        <SyllabusLeadModal open={brochureOpen} onOpenChange={setBrochureOpen} program={program} downloadUrl={gateFile || program.syllabus_url || program.brochure_url || ""} />
      </div>
    );
  }

  const selfCanonLegacy = !program.url_path && !(seo.canonical_url || "").trim();
  return (
    <div data-testid="program-detail-page">
      {selfCanonLegacy && (
      <JsonLd data={{
        "@context": "https://schema.org", "@type": "Course",
        name: program.title, description: program.description,
        provider: { "@type": "Organization", name: program.university },
        url: `${window.location.origin}/courses/${slug}`,
        ...(program.rating ? { aggregateRating: { "@type": "AggregateRating", ratingValue: program.rating, bestRating: 5 } } : {}),
      }} />
      )}

      {!hasSections && (
      <div className="relative h-72 sm:h-96">
        <img src={program.image} alt={(program.title || program.university || '').split('|')[0].trim()} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8">
            <Link to="/courses" className="text-slate-300 text-sm flex items-center gap-1.5 mb-4 hover:text-white" data-testid="back-to-programs"><ArrowLeft className="w-4 h-4" /> All programs</Link>
            <div className="flex flex-wrap gap-2 mb-3">
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-rose-600 text-white">{program.program_type || program.level}</span>
              {category && <Link to={`/domains/${category.slug}`} className="px-3 py-1 rounded-full text-xs font-semibold bg-white/15 text-white backdrop-blur border border-white/20 hover:bg-white/25">{category.name}</Link>}
              {program.free && <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-500 text-white">Free</span>}
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white">{program.title}</h1>
            <p className="text-slate-300 mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
              <Link to={`/institutes/${program.institute_slug}`} className="flex items-center gap-1.5 hover:text-white" data-testid="program-institute-link"><MapPin className="w-4 h-4" />{program.university} · {program.city}{program.country ? `, ${program.country}` : ""}</Link>
              {program.rating && <span className="flex items-center gap-1 text-amber-300"><Star className="w-4 h-4 fill-amber-300" />{program.rating}</span>}
              {program.learners && <span className="flex items-center gap-1"><Users className="w-4 h-4" />{program.learners}</span>}
            </p>
          </div>
        </div>
      </div>
      )}

      {hasSections && <SectionsRenderer sections={program.sections} slug={slug} />}
      <div className={hasSections ? "max-w-7xl mx-auto px-4 sm:px-6 py-10" : "max-w-7xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-1 lg:grid-cols-3 gap-10"}>
        {!hasSections && (
        <div className="lg:col-span-2 flex flex-col gap-10">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { icon: Clock, label: "Duration", value: `${program.duration_months} months` },
              { icon: Wallet, label: program.currency === "INR" ? "Program fee" : "Tuition / year", value: feeLabel(program) },
              { icon: CalendarDays, label: "Intakes", value: (program.intakes || []).join(", ") },
              { icon: MonitorSmartphone, label: "Mode", value: program.mode || "Online" },
            ].map((m, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`program-meta-${i}`}>
                <m.icon className="w-4 h-4 text-rose-600 mb-2" />
                <p className="text-xs text-slate-400">{m.label}</p>
                <p className="text-sm font-bold text-slate-900 mt-0.5">{m.value}</p>
              </div>
            ))}
          </div>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4">About this program</h2>
            <p className="text-slate-600 leading-relaxed">{program.description}</p>
          </section>

          {(program.skills || []).length > 0 && (
            <section>
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4">Skills you'll build</h2>
              <div className="flex flex-wrap gap-2">
                {program.skills.map((s) => (
                  <Link key={s} to={`/courses?skill=${encodeURIComponent(s)}`} className="px-4 py-2 rounded-full bg-slate-100 text-slate-700 text-sm font-semibold border border-slate-200 hover:border-rose-300 hover:text-rose-700 transition-colors" data-testid={`skill-chip-${s.replace(/\s+/g, "-")}`}>{s}</Link>
                ))}
              </div>
            </section>
          )}

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4">Highlights</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(program.highlights || []).map((h, i) => (
                <div key={i} className="flex items-start gap-2.5 p-4 rounded-xl bg-rose-50/60 border border-rose-100" data-testid={`highlight-${i}`}>
                  <CheckCircle2 className="w-4 h-4 text-rose-600 mt-0.5 shrink-0" />
                  <p className="text-sm text-slate-700">{h}</p>
                </div>
              ))}
            </div>
          </section>

          {program.min_percentage > 0 || (program.required_tests || []).length > 0 ? (
            <section>
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4">Eligibility criteria</h2>
              <div className="rounded-2xl border border-slate-200 bg-white p-6 flex flex-col gap-3" data-testid="eligibility-criteria">
                {program.min_percentage > 0 && <div className="flex justify-between text-sm"><span className="text-slate-500">Minimum academic score</span><span className="font-bold text-slate-900">{program.min_percentage}%</span></div>}
                {program.min_work_exp > 0 && <div className="flex justify-between text-sm"><span className="text-slate-500">Work experience</span><span className="font-bold text-slate-900">{program.min_work_exp}+ years</span></div>}
                {(program.required_tests || []).length > 0 && (
                  <div className="flex justify-between text-sm items-start">
                    <span className="text-slate-500">Accepted tests (any one)</span>
                    <span className="font-bold text-slate-900 text-right">{program.required_tests.map((t) => `${t.name} ${t.min}+`).join(" · ")}</span>
                  </div>
                )}
                <p className="text-xs text-slate-400 mt-2">Your exact eligibility is computed instantly when you apply — rules first, counselor review second.</p>
              </div>
            </section>
          ) : null}

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4">Career outcomes</h2>
            <div className="flex flex-wrap gap-2.5">
              {(program.outcomes || []).map((o, i) => (
                <span key={i} className="px-4 py-2 rounded-full bg-slate-100 text-slate-700 text-sm font-semibold border border-slate-200">{o}</span>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4">Frequently asked questions</h2>
            {clientReady ? <Accordion type="single" collapsible data-testid="program-faq">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} data-testid={`program-faq-${i}`}>
                  <AccordionTrigger className="text-left font-semibold text-slate-800">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion> : (
              <div data-testid="program-faq">
                {faqs.map((f, i) => (
                  <div key={i} className="border-b" data-testid={`program-faq-${i}`}>
                    <div className="flex flex-1 items-center justify-between py-4 font-medium text-left text-slate-800">
                      {f.q}<ChevronDown className="h-4 w-4 shrink-0" />
                    </div>
                    <div hidden>{f.a}</div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
        )}

        <aside className={hasSections ? "max-w-xl mx-auto w-full" : ""}>
          <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm lg:sticky lg:top-24" data-testid="apply-card">
            <p className="text-xs font-semibold uppercase tracking-widest text-rose-600">{program.free ? "Enrollment" : "Application fee"}</p>
            <p className="text-3xl font-extrabold text-slate-900 mt-1">{program.application_fee ? `₹${Number(program.application_fee).toLocaleString("en-IN")}` : "Free"}</p>
            {!program.free && <p className="text-xs text-slate-400 mt-1">Credited toward your program fee on enrollment.</p>}
            {program.emi_starts_at > 0 && <p className="text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 mt-3">EMI from ₹{Number(program.emi_starts_at).toLocaleString("en-IN")}/month</p>}
            <Button className="w-full mt-6 bg-gradient-to-r from-rose-600 to-rose-800 hover:from-rose-700 hover:to-rose-900 text-white font-bold rounded-xl py-6 shadow-lg" onClick={apply} disabled={applying} data-testid="apply-now-button">
              {applying ? "Starting…" : program.free ? "Enroll free" : "Apply now"}
            </Button>
            <SyllabusLeadModal open={brochureOpen} onOpenChange={setBrochureOpen} program={program} downloadUrl={program.syllabus_url || program.brochure_url || ""} />
            <Button variant="outline" className="w-full mt-3 rounded-xl py-6 font-semibold" onClick={() => setBrochureOpen(true)} data-testid="download-brochure-button">
              <Download className="w-4 h-4 mr-1.5" /> Download brochure
            </Button>
            <Button variant="outline" className="w-full mt-3 rounded-xl py-6 font-semibold text-rose-700 border-rose-200 hover:bg-rose-50" onClick={() => setCallbackOpen(true)} data-testid="detail-callback-button">
              <PhoneCall className="w-4 h-4 mr-1.5" /> Talk to a counselor
            </Button>
            <button className="w-full mt-3 text-sm font-semibold text-slate-500 hover:text-rose-600 flex items-center justify-center gap-1.5 py-2" onClick={toggleSave} data-testid="detail-save-button">
              <Bookmark className={`w-4 h-4 ${saved ? "fill-rose-600 text-rose-600" : ""}`} /> {saved ? "Saved" : "Save for later"}
            </button>
            <div className="mt-5 pt-5 border-t border-slate-100 flex flex-col gap-2.5 text-xs text-slate-500">
              <p className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Instant eligibility check</p>
              <p className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Save & resume application</p>
              <p className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Dedicated counselor after payment</p>
            </div>
          </div>
        </aside>
      </div>

      {program.related?.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-6">Similar programs</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {program.related.map((p) => <CourseCard key={p.slug} program={p} />)}
          </div>
        </div>
      )}

      <InquiryModal open={inquiryOpen} onOpenChange={setInquiryOpen} programSlug={slug} programTitle={program.title} />
      <CallbackModal open={callbackOpen} onOpenChange={setCallbackOpen} programSlug={slug} programTitle={program.title} />
    </div>
  );
}
